import { LineChartOutlined } from "@ant-design/icons"
import { Card, Empty, Flex, Skeleton, Typography } from "antd"
import React, { useCallback, useEffect } from "react"
import { type ConnectedProps, connect } from "react-redux"
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis
} from "recharts"

import type { RootState } from "../../../../../../store.ts"
import { fetchChecklistItemsChartData } from "../../../../redux/project-checklist/project-checklist.thunks.ts"

interface ProjectViewChecklistsChartSectionProps
  extends ConnectedProps<typeof connector> {
  projectId: string
}

function ProjectViewChecklistsChartSection({
  projectId,
  data = [],
  fetching,
  getChecklistItemsChartData
}: ProjectViewChecklistsChartSectionProps) {
  const handleGetChecklistItemsChartData = useCallback(() => {
    getChecklistItemsChartData({ project: projectId }).unwrap()
  }, [getChecklistItemsChartData, projectId])

  useEffect(() => {
    void handleGetChecklistItemsChartData()
  }, [handleGetChecklistItemsChartData])

  if (fetching) {
    return <Skeleton.Node className={"w-full h-[393px]"} active />
  }

  return (
    <Card
      className="overflow-hidden"
      title={
        <Flex align="center" gap={12}>
          <div className={"py-1 px-2 rounded-md aspect-square bg-primary/20"}>
            <LineChartOutlined className={"text-primary"} />
          </div>

          <Flex vertical>
            <Typography.Text strong>Burndown Chart</Typography.Text>
            <Typography.Text type="secondary" className="text-xs">
              Tasks remaining over time
            </Typography.Text>
          </Flex>
        </Flex>
      }
    >
      {data?.length ? (
        <ResponsiveContainer width="100%" height={288}>
          <ComposedChart
            data={data}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="actualFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#5BA4A4" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#5BA4A4" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#f0f0f0"
              vertical={false}
            />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 12, fill: "#8c8c8c" }}
              axisLine={{ stroke: "#f0f0f0" }}
              tickLine={false}
            />
            <YAxis
              domain={[0, "dataMax"]}
              // tickCount={10}
              tick={{ fontSize: 12, fill: "#8c8c8c" }}
              axisLine={false}
              tickLine={false}
              label={{
                value: "Tasks Remaining",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 12, fill: "#8c8c8c" }
              }}
            />
            <RechartsTooltip
              contentStyle={{
                borderRadius: 8,
                border: "1px solid #f0f0f0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
              }}
            />
            <Legend
              verticalAlign="top"
              align="right"
              iconType="circle"
              wrapperStyle={{ fontSize: 12, paddingBottom: 10 }}
            />
            <Line
              type="monotone"
              dataKey="ideal"
              name="Ideal Burndown"
              stroke="#c0c0c0"
              strokeDasharray="6 4"
              strokeWidth={1.5}
              dot={{ r: 3, fill: "#c0c0c0", stroke: "#c0c0c0" }}
              connectNulls
            />
            <Area
              type="monotone"
              dataKey="actual"
              name="Actual Remaining"
              stroke="#3B8686"
              strokeWidth={2.5}
              fill="url(#actualFill)"
              dot={{ r: 4, fill: "#3B8686", stroke: "#fff", strokeWidth: 2 }}
              connectNulls={false}
              activeDot={{
                r: 6,
                fill: "#3B8686",
                stroke: "#fff",
                strokeWidth: 2
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <Flex
          vertical
          justify={"center"}
          align={"center"}
          className={"h-[289px]"}
        >
          <Empty className={""} />
        </Flex>
      )}
    </Card>
  )
}

const mapStateToProps = (state: RootState) => ({
  data: state.project.checklistItemsChart,
  fetching: state.project.fetchingChecklistItemsChart
})
const mapDispatchToProps = {
  getChecklistItemsChartData: fetchChecklistItemsChartData
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewChecklistsChartSection)
